<script setup lang="ts">
import { computed } from 'vue'
import type { MungerInversionReport } from '@/api/client'

const props = defineProps<{
  mungerInversion: MungerInversionReport
}>()

const VERDICT_META: Record<string, { label: string; type: 'success' | 'warning' | 'danger' }> = {
  safe: { label: '安全', type: 'success' },
  caution: { label: '关注', type: 'warning' },
  deadly: { label: '致命', type: 'danger' },
}

const verdictMeta = computed(() => VERDICT_META[props.mungerInversion.safety_verdict] || { label: props.mungerInversion.safety_verdict, type: 'warning' as const })

interface Gauge {
  key: string
  name: string
  value: number | null | undefined
  display: string
  /** 是否触发 (越界为真) */
  breached: boolean
  redline: string
}

const snap = computed(() => props.mungerInversion.metrics_snapshot || {})

function num(v: any): number | null {
  return typeof v === 'number' && !Number.isNaN(v) ? v : null
}

/** Altman Z: 低更危险; Beneish M: 高更危险; 比率类按各自红线方向 */
const gauges = computed<Gauge[]>(() => {
  const s = snap.value
  const altman = num(s.altman_z)
  const beneish = num(s.beneish_m)
  const goodwill = num(s.goodwill_ratio)
  const pledge = num(s.pledge_ratio)
  const cashDebt = num(s.cash_to_short_debt)
  const coverage = num(s.interest_coverage)
  return [
    {
      key: 'altman',
      name: 'Altman Z-Score',
      value: altman,
      display: altman != null ? altman.toFixed(3) : '-',
      breached: altman != null && altman < 1.8,
      redline: 'Z < 1.8 红线',
    },
    {
      key: 'beneish',
      name: 'Beneish M-Score',
      value: beneish,
      display: beneish != null ? beneish.toFixed(3) : '-',
      breached: beneish != null && beneish > -1.78,
      redline: 'M > -1.78 存疑',
    },
    {
      key: 'goodwill',
      name: '商誉/净资产',
      value: goodwill,
      display: goodwill != null ? `${goodwill.toFixed(1)}%` : '-',
      breached: goodwill != null && goodwill > 40,
      redline: '> 40% 红线 / 30% 告警',
    },
    {
      key: 'pledge',
      name: '大股东质押率',
      value: pledge,
      display: pledge != null ? `${pledge.toFixed(1)}%` : '-',
      breached: pledge != null && pledge > 50,
      redline: '> 50% 红线 / 30% 告警',
    },
    {
      key: 'cash_debt',
      name: '现金短债比',
      value: cashDebt,
      display: cashDebt != null ? cashDebt.toFixed(2) : '-',
      breached: cashDebt != null && cashDebt < 0.6,
      redline: '< 0.6 红线',
    },
    {
      key: 'coverage',
      name: '利息覆盖倍数',
      value: coverage,
      display: coverage != null ? coverage.toFixed(2) : '-',
      breached: coverage != null && coverage < 2.0,
      redline: '< 2.0 红线',
    },
  ]
})

const hasAnyData = computed(() => gauges.value.some((g) => g.value != null))
</script>

<template>
  <el-card
    shadow="never"
    class="munger-panel"
    :class="{ 'munger-veto': mungerInversion.veto_triggered }"
  >
    <template #header>
      <div class="header-bar">
        <span class="title">🧨 芒格逆向排雷仪表 (Munger Inversion — 反过来想)</span>
        <div class="header-tags">
          <el-tag :type="verdictMeta.type" effect="dark" size="small">
            {{ verdictMeta.label }}
          </el-tag>
          <el-tag v-if="mungerInversion.veto_triggered" type="danger" effect="dark" size="small">
            🚫 一票否决熔断
          </el-tag>
        </div>
      </div>
    </template>

    <!-- 熔断醒目红色警示 -->
    <el-alert
      v-if="mungerInversion.veto_triggered"
      title="芒格逆向一票否决已触发 — 触发致命毁灭红线, 终审动作已强制转为卖出/规避"
      type="error"
      :closable="false"
      show-icon
      class="veto-alert"
    />

    <!-- 排雷指标仪表格 -->
    <div v-if="hasAnyData" class="gauge-grid">
      <div
        v-for="g in gauges"
        :key="g.key"
        class="gauge"
        :class="{ 'gauge-breached': g.breached, 'gauge-empty': g.value == null }"
      >
        <div class="gauge-name">{{ g.name }}</div>
        <div class="gauge-value">{{ g.display }}</div>
        <div class="gauge-redline">{{ g.redline }}</div>
      </div>
    </div>
    <el-empty v-else description="排雷指标数据缺失 (财务报表不可用)" :image-size="48" />

    <!-- 致命缺陷 -->
    <div v-if="mungerInversion.fatal_flaws?.length" class="flaws">
      <div class="sub-title danger">🚫 致命毁灭缺陷 (红线)</div>
      <div v-for="(f, i) in mungerInversion.fatal_flaws" :key="i" class="flaw-item">{{ f }}</div>
    </div>

    <!-- 预警信号 -->
    <div v-if="mungerInversion.warning_signals?.length" class="warnings">
      <div class="sub-title warning">⚠️ 预警风险</div>
      <div v-for="(w, i) in mungerInversion.warning_signals" :key="i" class="warn-item">{{ w }}</div>
    </div>
  </el-card>
</template>

<style scoped>
.munger-panel { display: flex; flex-direction: column; gap: 10px; }
.munger-veto { border: 2px solid var(--danger); }
.header-bar { display: flex; justify-content: space-between; align-items: center; width: 100%; }
.header-tags { display: flex; gap: 6px; }
.title { font-size: 15px; font-weight: 600; }
.veto-alert { margin-bottom: 4px; }
.gauge-grid {
  display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 10px;
}
.gauge {
  border: 1px solid var(--border-color); border-radius: 8px; padding: 10px 12px;
  text-align: center; background: var(--bg-elevated);
}
.gauge-breached {
  border-color: var(--danger); background: var(--bg-danger-subtle, rgba(234, 67, 53, 0.08));
}
.gauge-empty { opacity: 0.55; }
.gauge-name { font-size: 12px; color: var(--text-secondary); }
.gauge-value { font-size: 20px; font-weight: 800; color: var(--text-primary); margin: 4px 0; }
.gauge-breached .gauge-value { color: var(--danger); }
.gauge-redline { font-size: 11px; color: var(--text-muted); }
.sub-title { font-size: 13px; font-weight: 600; margin-bottom: 6px; }
.sub-title.danger { color: var(--danger); }
.sub-title.warning { color: var(--warning); }
.flaw-item {
  font-size: 13px; color: var(--danger); line-height: 1.6; padding: 6px 10px;
  background: var(--bg-danger-subtle, rgba(234, 67, 53, 0.08));
  border-radius: 6px; margin-bottom: 6px;
}
.warn-item {
  font-size: 13px; color: var(--warning); line-height: 1.6; padding: 6px 10px;
  background: var(--bg-tertiary); border-radius: 6px; margin-bottom: 6px;
}
</style>
